import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/auth";
import mockData from "./mock.json";
import api from "services/api";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";

import { GraphCity } from "./GraphCity";
import { GraphGender } from "./GraphGender";
import { GraphOvertime } from "./GraphOvertime";
import { GraphTotal } from "./GraphTotal";
import FooterLogo from "components/footer-logo";

import { IDate, ISummary } from "./types";
import { Main, SectionTitle, SplitRow } from "./styled";
import { GraphDates } from "./GraphDates";
import ESocialConnect from "pages/authentication/collaborators/e-social-connect";
import CollaboratorsKpiBar from "pages/authentication/collaborators/resume/kpi-bar";
import CollaboratorsActionItems from "pages/authentication/collaborators/resume/action-items";
import { IResumoSummary } from "pages/authentication/collaborators/resume/types";

export function CollaboratorsSummary() {
  const auth = useAuth();
  const organizationCnpj = auth.user?.organization?.cnpj;

  const [data, date, resumo] = useQueries({
    queries: [
      {
        queryKey: ['collaboratorsSummary', 'data'],
        queryFn: () => api.get<{ data: ISummary[] }>(`/v2/cdp/summary/${organizationCnpj}`),
        //@ts-ignore
        select: (data) => data.data.data
      },
      {
        queryKey: ['collaboratorsSummary', 'date'],
        queryFn: () => api.get<{ data: IDate[] }>(`/v2/cdp/summary-dates/${organizationCnpj}`),
        //@ts-ignore
        select: (data) => data.data.data
      },
      {
        queryKey: ['collaboratorsSummary', 'resumo', organizationCnpj],
        queryFn: () => api.get<{ data: IResumoSummary }>(`/v2/cdp/collaborators-resumo-summary/${organizationCnpj?.replace(/\D/g, '')}`),
        //@ts-ignore
        select: (data) => data.data.data,
        enabled: !!organizationCnpj,
      },
    ],
  })

  // Intentionally excludes resumo from page-level loading — its Snowflake queries are slower,
  // so KPI/action-items render skeleton states independently for progressive loading UX.
  const isLoading = (data.isFetching && !data.isFetched) || (date.isFetching && !date.isFetched);

  if (isLoading) {
    return (
      <Main>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={3}
        >
          <CircularProgress size={30} thickness={4} />
        </Box>
      </Main>
    );
  }

  if (data.isError || date.isError) {
    return (
      <Main>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={3}
          textAlign="center"
        >
          <ErrorOutline sx={{ fontSize: 40, color: 'error.main' }} />
          <Typography variant="h6" color="textPrimary">
            Ops! Algo deu errado
          </Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={400}>
            Não foi possível carregar os dados dos colaboradores no momento. 
            Por favor, tente novamente.
          </Typography>
        </Box>
      </Main>
    );
  }

  // if (validate?.data?.validate === null) {
  //   return <ESocialConnect />
  // }

  const resumoSummary = (resumo.data as IResumoSummary | undefined) ?? null;
  const employees = data.data?.filter((b: ISummary) => b.type === "EMPLOYEE") ?? [];

  return (
    <>
      {data.data && date.data && (
        <Main style={{ marginBottom: "1.5rem" }}>
          <GraphDates dates={date.data} />
        </Main>
      )}
      {/* <Main>
        <SectionTitle>KPIs</SectionTitle>
        <CollaboratorsKpiBar resumoSummary={resumoSummary} />
        <CollaboratorsActionItems resumoSummary={resumoSummary} />
      </Main> */}
      {data.data && date.data && (
        <>
          <Main>
            <SectionTitle>Dados Demográficos</SectionTitle>
            <GraphTotal beneficiaries={data.data} />
            <GraphOvertime beneficiaries={data.data} />
            <SplitRow>
              <div style={{ flex: 1 }}><GraphGender beneficiaries={employees} /></div>
              <div style={{ flex: 1 }}><GraphCity beneficiaries={employees} /></div>
            </SplitRow>
          </Main>
          <FooterLogo />
        </>
      )}
    </>
  );
}
